<template>
  <div class="chart-card chart-card-short">
    <h5>{{ chartTitle }}</h5>

    <div class="chart-container">
      <canvas ref="chartEl"></canvas>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue"
import Chart from "chart.js/auto"
import { getDiscreteChartColors } from "@/utils/chartUtils"
import { compareDrivetrain } from "@/constants/fieldOrders"

const props = defineProps({
  vouchers: { type: Array, default: () => [] },
  program: { type: String, default: "core" },
})

const chartEl = ref(null)
let chart = null

const countLabel = computed(() =>
  props.program === "sore" ? "Equipment Units" : "Vouchers"
)

const chartTitle = "Vouchers by Drivetrain Category"
  
const totalCount = computed(() => props.vouchers.length)

function fmtMoney(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value ?? 0)
}

const chartData = computed(() => {
  const rows = props.vouchers ?? []
  const totals = {}

  for (const row of rows) {
    const type = row.drivetrain || "No data"
    const funding = Number(row.fundingAmount ?? 0)
    totals[type] ??= { count: 0, funding: 0 }
    totals[type].count += 1
    totals[type].funding += Number.isFinite(funding) ? funding : 0
  }

  const equipmentTypes = Object.keys(totals).sort(compareDrivetrain)
  const colors = getDiscreteChartColors()

  return {
    labels: [countLabel.value],
    datasets: equipmentTypes.map((type, index) => ({
      label: type,
      data: [totals[type].count],
      fundingTotal: totals[type].funding,
      backgroundColor: colors[index % colors.length],
      borderColor: "transparent",
      borderWidth: 0,
    })),
  }
})

function renderChart() {
  if (!chartEl.value) return

  if (chart) {
    chart.destroy()
    chart = null
  }

  chart = new Chart(chartEl.value, {
    type: "bar",
    data: chartData.value,
    options: {
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      // animations: {
      //   colors: false,
      //   x: false,
      //   y: false,
      // },
      plugins: {
        legend: {
          display: false, // use your shared EquipmentTypeLegend instead
        },
        tooltip: {
          callbacks: {
            title(items) {
              return items[0]?.dataset?.label ?? ""
            },
            label(context) {
              const count = context.raw ?? 0
              const pct = totalCount.value > 0 ? ((count / totalCount.value) * 100).toFixed(1) : 0
              const funding = context.dataset?.fundingTotal ?? 0
              return [
                    `${countLabel.value}: ${count.toLocaleString()}`,
                    `Funding: ${fmtMoney(funding)}`,
                    `${pct}% of total ${countLabel.value.toLowerCase()}`,
                ]
            },
          },
        },
      },

      scales: {
        x: {
          stacked: true,
          title: {
            display: true,
            text: `Number of ${countLabel.value}`,
          },
          grid: {
            color: "rgba(0,0,0,0.08)",
          },
        },
        y: {
          stacked: true,
          display: false,
          grid: {
            display: false,
          },
        },
      },
    },
  })
}

onMounted(renderChart)

watch(
  chartData,
  () => {
    renderChart()
  },
  { deep: true }
)

onBeforeUnmount(() => {
  if (chart) chart.destroy()
})
</script>

<style scoped>
.chart-card {
  background: white;
  border-radius: 0;
  margin-top: 3rem;
}

.chart-card-short {
  height: 120px;
}

.chart-container {
  height: 100px;
}

canvas {
  width: 100%;
  height: 100%;
}
</style>